/**
 * Pure state helpers for the race workspace: patch merging (mirrors the backend
 * `apply_patches`), motion sample stores, and per-frame interpolation along the
 * layout path. No race logic lives here; every number comes from the engine.
 */
import { circularDelta } from "./geometry";

export const MOTION_FIELDS = ["s", "progress", "branch", "quality", "x", "y", "speed", "pit"];
export const BRANCHES = ["track", "pit_lane", "off_track"];
export const QUALITIES = ["observed", "projected", "estimated_from_timing", "unavailable"];
export const EXTRAPOLATION_HORIZON_SECONDS = 2.5;
export const STALE_AFTER_SECONDS = 6;

// ---------------------------------------------------------------------------------
// Temporal status rules — a line-for-line mirror of backend/race_engine/status_rules.py.
// A car's status at playback time t is a pure function of persisted row fields and t,
// so seeking, linear playback and the server merge can never disagree.
// ---------------------------------------------------------------------------------
export const STOPPED_AFTER_SECONDS = 240;
export const STOPPED_MIN_LAPS_DOWN = 2;
export const LONG_PIT_STAY_SECONDS = 600;
const FINISHED_PHASES = ["chequered", "finished"];

export function inPitAt(row, t) {
    const window = row?.pit_window_s;
    if (window && window[0] != null && window[1] != null && window[0] <= t && t <= window[1]) return true;
    const lane = row?.pit_lane_s;
    if (lane && lane[0] != null && lane[0] <= t && (lane[1] == null || t < lane[1])) return true;
    return false;
}

export function deriveEntryStatus(row, t, { phase, leaderLaps, raceLike }) {
    const final = row?.final;
    if (final) {
        if (final.dsq) return ["dsq", "disqualified (official classification)"];
        if (final.dns) return ["dns", "did not start (official classification)"];
        if (final.dnf) return ["retired", "did not finish (official classification)"];
        if (FINISHED_PHASES.includes(phase) && final.position) return ["finished", "classified"];
    }
    const finishedS = row?.finished_s;
    if (FINISHED_PHASES.includes(phase) && raceLike && finishedS != null && t >= finishedS) return ["finished", "took the chequered flag"];
    if (inPitAt(row, t)) {
        const lane = row?.pit_lane_s;
        if (lane && lane[0] != null && lane[1] == null && t - lane[0] > LONG_PIT_STAY_SECONDS) return ["in_pit", `in the pit lane for ${Math.floor((t - lane[0]) / 60)} min`];
        return ["in_pit", null];
    }
    const progressS = row?.progress_s;
    if (raceLike && phase !== "pre_session" && progressS != null) {
        const quiet = t - progressS;
        if (quiet > STOPPED_AFTER_SECONDS && (leaderLaps || 0) - (row.laps_completed || 0) >= STOPPED_MIN_LAPS_DOWN) {
            return ["stopped", `no timing or location updates for ${Math.floor(quiet / 60)} min`];
        }
    }
    return ["running", null];
}

/** Rewrite status/status_note on every entry row of a snapshot at time t (t defaults to the snapshot clock). */
export function applyDerivedStatus(state, t = null) {
    const at = t != null ? t : state?.clock?.session_seconds;
    if (at == null || !state?.entries) return state;
    const context = { phase: state.phase?.phase, leaderLaps: state.leader_laps, raceLike: !!state.session?.is_race_like };
    let changed = false;
    const entries = state.entries.map((row) => {
        const [status, note] = deriveEntryStatus(row, at, context);
        if (status === row.status && (note ?? null) === (row.status_note ?? null)) return row;
        changed = true;
        return { ...row, status, status_note: note };
    });
    return changed ? { ...state, entries } : state;
}

function isoToMicros(value) {
    if (!value) return null;
    const match = String(value).match(/^(\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d)(?:\.(\d{1,6}))?(Z|[+-]\d\d:?\d\d)?$/);
    if (!match) return null;
    const millis = Date.parse(`${match[1]}${match[3] || "Z"}`);
    if (Number.isNaN(millis)) return null;
    return millis * 1000 + Number((match[2] || "").padEnd(6, "0"));
}

function microsToIso(micros) {
    const whole = Math.floor(micros / 1e6);
    const fraction = micros - whole * 1e6;
    return `${new Date(whole * 1000).toISOString().slice(0, 19)}${fraction ? `.${String(fraction).padStart(6, "0")}` : ""}+00:00`;
}

/**
 * Mirror of status_rules.materialized_clock: the clock of a materialized state describes time t (the requested
 * time, else the last applied patch time); race_seconds is session_seconds minus clock.race_start, in whole
 * microseconds so client and server agree bit for bit. event_time/watermark stay as the carrying snapshot sent them.
 */
export function applyDerivedClock(state, t = null) {
    if (!state) return state;
    const previous = state.clock || {};
    const clock = { ...previous };
    const dateStart = isoToMicros(state.session?.date_start);
    if (t != null) {
        clock.session_seconds = Number(t);
        if (dateStart != null) clock.playback_time = microsToIso(dateStart + Math.round(Number(t) * 1e6));
    }
    const raceStart = isoToMicros(clock.race_start);
    if (raceStart == null) clock.race_seconds = null;
    else if (clock.session_seconds != null && dateStart != null) clock.race_seconds = (Math.round(clock.session_seconds * 1e6) - (raceStart - dateStart)) / 1e6;
    const same = ["session_seconds", "playback_time", "race_seconds"].every((key) => (clock[key] ?? null) === (previous[key] ?? null));
    return same ? state : { ...state, clock };
}

/**
 * Pure merge of ordered patches into a snapshot copy, with no time derivation: the clock carries the last applied
 * patch's time and statuses are whatever the rows carried. Returns the input object when nothing applied.
 */
export function mergePatches(snapshot, patches, upToSeconds = null) {
    if (!snapshot) return snapshot;
    const state = { ...snapshot, phase: { ...(snapshot.phase || {}) }, clock: { ...(snapshot.clock || {}) } };
    const rows = new Map((state.entries || []).map((row) => [row.entry_id, { ...row }]));
    const raceControl = [...(state.race_control || [])];
    let lastSeq = state.sequence || 0;
    let touched = false;
    for (const patch of patches || []) {
        if (upToSeconds != null && patch.s != null && patch.s > upToSeconds) break;
        const fields = patch.fields || {};
        switch (patch.kind) {
            case "entry": {
                const row = rows.get(patch.key);
                if (row) rows.set(patch.key, { ...row, ...fields });
                break;
            }
            case "phase":
                state.phase = { ...state.phase, ...fields };
                if ("race_start" in fields) state.clock.race_start = fields.race_start;
                break;
            case "race_control":
                raceControl.push(fields);
                break;
            case "weather":
                state.weather = Object.keys(fields).length ? fields : null;
                break;
            case "derived":
                Object.assign(state, fields);
                break;
            case "header":
                Object.assign(state, fields);
                break;
            default:
                break;
        }
        touched = true;
        lastSeq = patch.seq ?? lastSeq;
        if (patch.t) state.clock.playback_time = patch.t;
        if (patch.s != null) state.clock.session_seconds = patch.s;
    }
    if (!touched) return snapshot;
    state.race_control = raceControl.slice(-40);
    state.sequence = lastSeq;
    const order = state.order || [];
    const ordered = order.filter((id) => rows.has(id)).map((id) => rows.get(id));
    const rest = [...rows.values()].filter((row) => !order.includes(row.entry_id));
    state.entries = ordered.concat(rest);
    return state;
}

/** Merge then derive the clock and every time-derived status at upToSeconds (the server's apply_patches). */
export function applyPatches(snapshot, patches, upToSeconds = null) {
    if (!snapshot) return snapshot;
    const merged = mergePatches(snapshot, patches, upToSeconds);
    return applyDerivedStatus(applyDerivedClock(merged, upToSeconds), upToSeconds);
}

/** Stamp a materialized state with the cursor it describes and the time of the last patch applied to it. */
export function stampMaterialized(snapshot, t, appliedThroughS) {
    const stamped = applyDerivedClock(snapshot, t);
    return { ...stamped, clock: { ...stamped.clock, applied_through_s: appliedThroughS ?? null } };
}

/**
 * Synchronous, incremental materialization of replay state at the playback cursor.
 *
 * One authoritative cursor drives every panel: the state at t is the nearest checkpoint at or before t plus the
 * ordered patches whose effective time s satisfies checkpoint ≤ s ≤ t (closed window), with time-derived statuses
 * re-derived at t. Moving forward applies only the patches in (applied, t]; moving backward, or into another
 * checkpoint window, re-materializes from that checkpoint. ``stateAt`` returns the same snapshot object when nothing
 * changed, so React consumers re-render at the cadence of real state changes, not per animation frame. Decoded
 * checkpoints and timing chunks are handed in by the loader; ``missing`` says what t still needs.
 */
export class ReplayMaterializer {
    constructor(manifest) {
        this.manifest = manifest;
        this.checkpoints = new Map();
        this.chunks = new Map();
        this.reset();
    }

    reset() {
        this.ckIndex = null;
        this.base = null;
        this.patches = [];
        this.lastChunk = null;
        this.applied = 0;
        this.appliedThroughS = null;
        this.snapshot = null;
        this.derivedAt = null;
    }

    setCheckpoint(index, snapshot) {
        if (snapshot) this.checkpoints.set(index, snapshot);
    }

    setChunk(index, payload) {
        if (payload) this.chunks.set(index, (payload.patches || []).filter((patch) => patch.s != null));
    }

    checkpointIndex(t) {
        const { origin_seconds: origin, checkpoint_seconds: ckpt } = this.manifest.time;
        return Math.max(0, Math.min(this.manifest.checkpoints.length - 1, Math.floor((t - origin) / ckpt)));
    }

    chunkIndex(t) {
        const { origin_seconds: origin, chunk_seconds: chunk } = this.manifest.time;
        return Math.max(0, Math.min(this.manifest.chunks.length - 1, Math.floor((t - origin) / chunk)));
    }

    /** Assets the state at t needs that are not loaded yet. */
    missing(t) {
        const ck = this.checkpointIndex(t);
        const needed = [];
        if (!this.checkpoints.has(ck)) needed.push({ kind: "checkpoint", index: ck });
        const ckSeconds = this.manifest.checkpoints[ck].session_seconds;
        for (let index = this.chunkIndex(ckSeconds); index <= this.chunkIndex(t); index += 1) if (!this.chunks.has(index)) needed.push({ kind: "timing", index });
        return needed;
    }

    /** Patches due at t (effective time in (applied, t]) that the displayed state does not yet carry: 0 unless assets are missing. */
    pendingAt(t) {
        if (this.missing(t).length) return null;
        if (this.ckIndex == null || this.checkpointIndex(t) !== this.ckIndex || (this.derivedAt != null && t < this.derivedAt)) return null;
        let count = 0;
        for (let index = this.lastChunk + 1; index <= this.chunkIndex(t); index += 1) count += (this.chunks.get(index) || []).filter((patch) => patch.s <= t).length;
        for (let i = this.applied; i < this.patches.length && this.patches[i].s <= t; i += 1) count += 1;
        return count;
    }

    /** {snapshot, changed, checkpoint, appliedThroughS} at t, or null while assets are missing. */
    stateAt(t) {
        if (this.missing(t).length) return null;
        const ck = this.checkpointIndex(t);
        const ckSeconds = this.manifest.checkpoints[ck].session_seconds;
        const firstChunk = this.chunkIndex(ckSeconds);
        const previous = this.snapshot;
        if (ck !== this.ckIndex || this.snapshot == null || (this.derivedAt != null && t < this.derivedAt)) {
            // New window, first use, or a backward move: start again from the checkpoint.
            this.ckIndex = ck;
            this.base = this.checkpoints.get(ck);
            this.patches = [];
            this.lastChunk = firstChunk - 1;
            this.applied = 0;
            this.appliedThroughS = null;
            this.snapshot = this.base;
        }
        for (let index = this.lastChunk + 1; index <= this.chunkIndex(t); index += 1) {
            (this.chunks.get(index) || []).forEach((patch) => patch.s >= ckSeconds && this.patches.push(patch));
            this.lastChunk = index;
        }
        let end = this.applied;
        while (end < this.patches.length && this.patches[end].s <= t) end += 1;
        let next = this.snapshot;
        if (end > this.applied) {
            next = mergePatches(next, this.patches.slice(this.applied, end));
            this.appliedThroughS = this.patches[end - 1].s;
            this.applied = end;
        }
        next = applyDerivedStatus(next, t);
        this.derivedAt = t;
        const changed = next !== previous;
        this.snapshot = next;
        return { snapshot: next, changed, checkpoint: ck, appliedThroughS: this.appliedThroughS };
    }
}

/** What changed between two materialized states, for the playback instrumentation: ["order", "status", "laps", "phase", "pit"]. */
export function summarizeTransition(previous, next) {
    if (!previous || !next || previous === next) return [];
    const kinds = [];
    if ((previous.order || []).join(",") !== (next.order || []).join(",")) kinds.push("order");
    if (previous.phase?.phase !== next.phase?.phase) kinds.push("phase");
    const before = new Map((previous.entries || []).map((row) => [row.entry_id, row]));
    let status = false;
    let laps = false;
    let pit = false;
    (next.entries || []).forEach((row) => {
        const old = before.get(row.entry_id);
        if (!old) return;
        if (old.status !== row.status) status = true;
        if (old.laps_completed !== row.laps_completed) laps = true;
        if ((old.pit_count || 0) !== (row.pit_count || 0)) pit = true;
    });
    if (status) kinds.push("status");
    if (laps) kinds.push("laps");
    if (pit) kinds.push("pit");
    if (!kinds.length && previous.sequence !== next.sequence) kinds.push("fields");
    return kinds;
}

/** Decode a compact motion row from a chunk or live batch. */
export function decodeMotionRow(row) {
    return {
        s: row[0],
        progress: row[1],
        branch: BRANCHES[row[2]] || "off_track",
        quality: QUALITIES[row[3]] || "unavailable",
        x: row[4],
        y: row[5],
        speed: row[6],
        pit: row[7],
    };
}

/**
 * Per-entry motion sample store with binary search by session seconds.
 * Samples are kept sorted and de-duplicated by time.
 */
export class MotionStore {
    constructor() {
        this.samples = new Map();
        this.loadedChunks = new Set();
    }

    reset() {
        this.samples = new Map();
        this.loadedChunks = new Set();
    }

    addChunk(chunkIndex, entries) {
        if (this.loadedChunks.has(chunkIndex)) return;
        this.loadedChunks.add(chunkIndex);
        Object.entries(entries || {}).forEach(([entryId, rows]) => {
            const decoded = rows.map(decodeMotionRow);
            this.addSamples(entryId, decoded);
        });
    }

    addSamples(entryId, decoded) {
        const existing = this.samples.get(entryId) || [];
        const merged = existing.concat(decoded).sort((a, b) => a.s - b.s);
        const deduped = [];
        merged.forEach((sample) => {
            if (!deduped.length || sample.s > deduped[deduped.length - 1].s) deduped.push(sample);
        });
        this.samples.set(entryId, deduped);
    }

    addLiveBatch(batch) {
        Object.entries(batch?.entries || {}).forEach(([entryId, rows]) => this.addSamples(entryId, rows.map(decodeMotionRow)));
    }

    trimBefore(seconds) {
        this.samples.forEach((list, entryId) => {
            const index = list.findIndex((sample) => sample.s >= seconds);
            if (index > 1) this.samples.set(entryId, list.slice(index - 1));
        });
    }

    /** Returns [before, after] samples around t (either may be null). */
    bracket(entryId, t) {
        const { prev, a, b } = this.around(entryId, t);
        void prev;
        return [a, b];
    }

    /** {prev, a, b}: the newest sample at or before t, the one after it, and the one before a (for reacquisition marks). */
    around(entryId, t) {
        const list = this.samples.get(entryId);
        if (!list || !list.length) return { prev: null, a: null, b: null };
        let lo = 0;
        let hi = list.length - 1;
        if (t < list[0].s) return { prev: null, a: null, b: list[0] };
        if (t >= list[hi].s) return { prev: hi > 0 ? list[hi - 1] : null, a: list[hi], b: null };
        while (lo < hi - 1) {
            const mid = (lo + hi) >> 1;
            if (list[mid].s <= t) lo = mid;
            else hi = mid;
        }
        return { prev: lo > 0 ? list[lo - 1] : null, a: list[lo], b: list[hi] };
    }

    latest(entryId) {
        const list = this.samples.get(entryId);
        return list && list.length ? list[list.length - 1] : null;
    }

    /** The most recent relocation (pair of consecutive samples) whose landing sample is within `window` seconds before t. */
    recentRelocation(entryId, t, window, lapLengthM) {
        const list = this.samples.get(entryId);
        if (!list || list.length < 2) return null;
        const { a } = this.around(entryId, t);
        if (!a) return null;
        let index = list.indexOf(a);
        while (index > 0 && t - list[index].s <= window) {
            if (isRelocation(list[index - 1], list[index], lapLengthM)) return { from: list[index - 1], to: list[index] };
            index -= 1;
        }
        return null;
    }
}

/** Bracketing samples further apart than this are a missing-data period: the car is drawn as an estimate. */
export const BRIDGE_GAP_SECONDS = 2.0;
/**
 * A pair of samples at least RELOCATION_MIN_SPAN_SECONDS apart implying more than this along the path is a provider
 * relocation (the feed jumped and stayed; e.g. 711 m in 1.9 s at Las Vegas 2025): the slide between them is an
 * estimate, not an observation. Shorter spans are left alone because ~0.1 s timestamp jitter inflates real speeds.
 */
export const RELOCATION_SPEED_MPS = 130;
export const RELOCATION_MIN_SPAN_SECONDS = 0.75;
/** After a relocation the car is drawn at the reacquired sample and marked for this long. */
export const REACQUIRED_MARK_SECONDS = 2.0;

/**
 * Two consecutive samples imply a provider relocation when, at least RELOCATION_MIN_SPAN_SECONDS apart, the larger of
 * the along-path distance and the straight-line chord between them exceeds RELOCATION_SPEED_MPS. The rendering
 * policy for such a pair is the same everywhere (marker, trail, tower, diagnostics): hold the earlier sample until
 * the later one is current, then show the car at the later one with a reacquisition mark. No slide is ever drawn.
 */
export function isRelocation(a, b, lapLengthM) {
    if (!a || !b) return false;
    const span = b.s - a.s;
    if (!(span >= RELOCATION_MIN_SPAN_SECONDS)) return false;
    const along = lapLengthM && a.branch === "track" && b.branch === "track" ? Math.abs(circularDelta(b.progress, a.progress)) * lapLengthM : 0;
    const chord = a.x != null && b.x != null ? Math.hypot(b.x - a.x, b.y - a.y) / 10 : 0; // layout units are 0.1 m
    return Math.max(along, chord) / span > RELOCATION_SPEED_MPS;
}

/**
 * Trail segments for a window of samples: a segment breaks at a missing-data gap (> BRIDGE_GAP_SECONDS) and at a
 * relocation, so no path is drawn where there is no evidence the car travelled. Each break records why.
 */
export function segmentTrail(samples, lapLengthM) {
    const segments = [];
    const breaks = [];
    let current = [];
    for (let i = 0; i < samples.length; i += 1) {
        const sample = samples[i];
        const previous = i > 0 ? samples[i - 1] : null;
        if (previous) {
            const gap = sample.s - previous.s;
            const relocation = isRelocation(previous, sample, lapLengthM);
            if (relocation || gap > BRIDGE_GAP_SECONDS) {
                if (current.length) segments.push(current);
                breaks.push({ from: previous, to: sample, reason: relocation ? "relocation" : "gap", seconds: gap });
                current = [];
            }
        }
        current.push(sample);
    }
    if (current.length) segments.push(current);
    return { segments, breaks };
}

/**
 * Rendered position for one entry at playback time t. Interpolates along the
 * path between samples; extrapolates briefly after the last sample; then holds
 * and marks stale. Never places a car from standings.
 *
 * `sampleAt`/`sampleAge` describe the newest observation at or before t (the
 * next sample is read for visual interpolation only); `gapSeconds` is the span
 * of the bracketing pair, so an interpolation across missing data is reported
 * as `bridged` rather than as a fresh observation. Before a car's first sample
 * nothing is drawn: showing it at a future sample would leak future knowledge.
 */
export function renderEntryMotion(store, entryId, t, lapLengthM) {
    const { prev, a, b } = store.around(entryId, t);
    if (!a && !b) return { status: "unavailable", quality: "unavailable" };
    if (!a) return { status: "unavailable", quality: "unavailable", nextSampleAt: b.s };
    const base = { sampleAt: a.s, sampleAge: t - a.s };
    void prev;
    // For REACQUIRED_MARK_SECONDS after a relocation the car is drawn from the reacquired samples; say so (marker, tower, diagnostics).
    const relocation = store.recentRelocation(entryId, t, REACQUIRED_MARK_SECONDS, lapLengthM);
    if (relocation && t >= relocation.to.s) {
        const { from, to } = relocation;
        base.reacquired = true;
        base.reacquiredFrom = { s: from.s, x: from.x, y: from.y, progress: from.progress };
        base.reacquiredAt = to.s;
        base.jumpM = Math.round(Math.max(lapLengthM && from.branch === "track" && to.branch === "track" ? Math.abs(circularDelta(to.progress, from.progress)) * lapLengthM : 0, from.x != null && to.x != null ? Math.hypot(to.x - from.x, to.y - from.y) / 10 : 0));
    }
    if (a && b) {
        const span = b.s - a.s;
        const ratio = span > 0 ? Math.max(0, Math.min(1, (t - a.s) / span)) : 1;
        const gap = { ...base, gapSeconds: span, nextSampleAt: b.s };
        if (span > STALE_AFTER_SECONDS * 2) {
            // Missing-data period longer than 12 s: hold the earlier sample rather than sweeping across the gap.
            return { ...gap, status: t - a.s <= STALE_AFTER_SECONDS ? "held" : "stale", quality: a.quality, branch: a.branch, progress: a.progress, x: a.x, y: a.y, pit: a.pit, speed: a.speed };
        }
        if (isRelocation(a, b, lapLengthM)) {
            // Provider relocation (the feed jumped and stayed), in any direction and on any branch: the earlier and the
            // later position cannot both be right and nothing says the car drove between them. Hold the earlier sample
            // until the later one is current; never animate the displacement.
            return { ...gap, reacquired: undefined, status: "held", relocation: true, reacquireAt: b.s, jumpM: Math.round(Math.max(lapLengthM && a.branch === "track" && b.branch === "track" ? Math.abs(circularDelta(b.progress, a.progress)) * lapLengthM : 0, a.x != null && b.x != null ? Math.hypot(b.x - a.x, b.y - a.y) / 10 : 0)), quality: a.quality, branch: a.branch, progress: a.progress, x: a.x, y: a.y, pit: a.pit, speed: a.speed };
        }
        // Interpolation policy: <= 2 s between samples is an observation; 2-12 s is a bridged estimate (drawn, ringed).
        const status = span > BRIDGE_GAP_SECONDS ? "bridged" : "interpolated";
        if (a.branch === "pit_lane" && b.branch === "pit_lane" && a.pit != null && b.pit != null) {
            return { ...gap, status, quality: b.quality, branch: "pit_lane", progress: ((a.progress + circularDelta(b.progress, a.progress) * ratio) % 1 + 1) % 1, pit: a.pit + (b.pit - a.pit) * ratio, speed: b.speed };
        }
        const delta = circularDelta(b.progress, a.progress);
        if (delta < -0.02 || a.branch !== b.branch || a.branch !== "track") {
            // Backwards movement or branch change: blend straight between the two points.
            return { ...gap, status, quality: b.quality, branch: b.branch, progress: b.progress, x: a.x + (b.x - a.x) * ratio, y: a.y + (b.y - a.y) * ratio, blend: true, speed: b.speed };
        }
        return { ...gap, status, quality: b.quality, branch: "track", progress: ((a.progress + delta * ratio) % 1 + 1) % 1, speed: b.speed };
    }
    const age = t - a.s;
    if (age <= EXTRAPOLATION_HORIZON_SECONDS && a.speed && a.branch === "track" && lapLengthM) {
        const advance = (a.speed * age) / lapLengthM;
        return { ...base, status: "extrapolated", quality: a.quality, branch: "track", progress: ((a.progress + advance) % 1 + 1) % 1, speed: a.speed };
    }
    return { ...base, status: age <= STALE_AFTER_SECONDS ? "held" : "stale", quality: a.quality, branch: a.branch, progress: a.progress, x: a.x, y: a.y, pit: a.pit, speed: a.speed };
}

export const POSITION_QUALITIES = ["observed", "projected", "estimated", "held", "unavailable"];

/**
 * One label for how trustworthy a drawn position is:
 * observed    between two samples <= BRIDGE_GAP_SECONDS apart, inside the track/pit corridor
 * projected   from samples off the corridor (run-off) drawn at their projected/raw point
 * estimated   extrapolated past the last sample, bridged across missing data, or from lap timing
 * held        the last sample, held still (held <= 6 s, then stale)
 * unavailable no sample at or before t
 */
export function positionQuality(motion) {
    if (!motion || motion.status === "unavailable") return "unavailable";
    if (motion.status === "held" || motion.status === "stale") return "held";
    if (motion.reacquired) return "estimated";
    if (motion.status === "extrapolated" || motion.status === "bridged" || motion.quality === "estimated_from_timing") return "estimated";
    if (motion.quality === "projected" || motion.branch === "off_track") return "projected";
    return "observed";
}

export function formatSessionClock(seconds, raceStartSeconds = null) {
    if (seconds == null || Number.isNaN(seconds)) return "--:--";
    const base = raceStartSeconds != null ? seconds - raceStartSeconds : seconds;
    const sign = base < 0 ? "-" : "";
    const value = Math.floor(Math.abs(base));
    const h = Math.floor(value / 3600);
    const m = Math.floor((value % 3600) / 60);
    const s = value % 60;
    return h > 0 ? `${sign}${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${sign}${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function formatLapTime(seconds) {
    if (seconds == null || !Number.isFinite(Number(seconds))) return "—";
    const value = Number(seconds);
    const m = Math.floor(value / 60);
    const s = (value - m * 60).toFixed(3);
    return `${m}:${s.padStart(6, "0")}`;
}

export function formatGapRow(row) {
    if (!row) return "—";
    if (row.gap_kind === "laps" && row.gap_to_leader_laps) return `+${row.gap_to_leader_laps} LAP${row.gap_to_leader_laps > 1 ? "S" : ""}`;
    if (row.gap_kind === "seconds" && row.gap_to_leader_seconds != null) {
        if (row.gap_to_leader_seconds === 0) return "Leader";
        return `+${Number(row.gap_to_leader_seconds).toFixed(3)}`;
    }
    return "—";
}

export function formatIntervalRow(row) {
    if (!row) return "—";
    if (row.interval_kind === "laps" && row.interval_laps) return `+${row.interval_laps} LAP${row.interval_laps > 1 ? "S" : ""}`;
    if (row.interval_kind === "seconds" && row.interval_seconds != null) {
        if (row.interval_seconds === 0) return "—";
        return `+${Number(row.interval_seconds).toFixed(3)}`;
    }
    return "—";
}

export const PHASE_LABELS = {
    pre_session: "Pre-session",
    formation_lap: "Formation lap",
    green: "Green",
    safety_car: "Safety car",
    safety_car_ending: "Safety car in",
    virtual_safety_car: "Virtual safety car",
    virtual_safety_car_ending: "VSC ending",
    red_flag: "Red flag",
    restart_pending: "Restart pending",
    chequered: "Chequered flag",
    finished: "Finished",
};
